import { useState, useEffect } from "react";
import { Button, Row, Form, Container, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import axios from "axios";
import Layout from "../../../layout/default";
import Block from "../../../components/Block/Block";

const MySwal = withReactContent(Swal);

function MyProfilePage() {
    const [userDetails, setUserDetails] = useState(null); // State to store user details
    const [loading, setLoading] = useState(true); // State for loading
    const [error, setError] = useState(null); // State for error
    const [updatedDetails, setUpdatedDetails] = useState({}); // State for updated details

    useEffect(() => {
        const userId = localStorage.getItem("user_id");
        const authToken = localStorage.getItem("authToken");

        if (userId && authToken) {
            axios
                .get(`${process.env.REACT_APP_HOST}api/users/details`, {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                })
                .then((response) => {
                    const userDetails = response.data.data.user_details;
                    setUserDetails(userDetails);
                    setUpdatedDetails(userDetails); // Initialize updated details with current user details
                    setLoading(false);
                    MySwal.fire({
                        title: "Success!",
                        text: "User details loaded successfully.",
                        icon: "success",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                })
                .catch(() => {
                    setError("Failed to load user details.");
                    setLoading(false);
                    MySwal.fire({
                        title: "Error!",
                        text: "Failed to load user details.",
                        icon: "error",
                        timer: 2000,
                        showConfirmButton: false,
                    });
                });
        } else {
            setError("User not found or unauthorized.");
            setLoading(false);
            MySwal.fire({
                title: "Unauthorized",
                text: "User not found or unauthorized.",
                icon: "warning",
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUpdatedDetails((prevDetails) => ({
            ...prevDetails,
            [name]: value,
        }));
    };

    const handleSaveChanges = () => {
        const authToken = localStorage.getItem("authToken");

        // Construct query parameters
        const params = new URLSearchParams({
            user_id: localStorage.getItem("user_id"), // Pass user_id from localStorage
            user_name: updatedDetails.first_name, // Changed from first_name to user_name
            last_name: updatedDetails.last_name,
            email: updatedDetails.email,
            phone_1: updatedDetails.phone_1,
            phone_2: updatedDetails.phone_2,
            referral_code: updatedDetails.referral_code,
            referred_by: updatedDetails.referred_by,
            aadhar_number: updatedDetails.aadhar_number,
            middle_name: updatedDetails.middle_name,
            pincode: updatedDetails.pincode,
        }).toString();

        axios
            .put(
                `${process.env.REACT_APP_HOST}api/users/edit?${params}`, // Pass parameters in the URL
                null, // No payload in the body, all data is passed as query params
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            )
            .then(() => {
                setUserDetails(updatedDetails); // Update the user details in state
                MySwal.fire({
                    title: "Success!",
                    text: "Profile updated successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            })
            .catch((error) => {
                const errorMessage =
                    error.response?.data?.message || "Failed to update profile.";
                MySwal.fire({
                    title: "Error!",
                    text: errorMessage,
                    icon: "error",
                    timer: 2000,
                    showConfirmButton: false,
                });
            });
    };

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
                <div className="text-danger text-center">{error}</div>
            </Container>
        );
    }

    return (
        <Layout title="My Profile" content="container">
            <Block>
                <Row className="shadow-lg border-0 mb-4" style={{ borderRadius: "15px" }}>
                    <h3 className="text-primary mb-4 text-center">Profile Details</h3>
                    <Form>
                        <Form.Group>
                            <Form.Label>User Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="user_name"
                                value={updatedDetails?.first_name || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="last_name"
                                value={updatedDetails?.last_name || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={updatedDetails?.email || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Phone 1</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone_1"
                                value={updatedDetails?.phone_1 || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Phone 2</Form.Label>
                            <Form.Control
                                type="text"
                                name="phone_2"
                                value={updatedDetails?.phone_2 || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Referral Code</Form.Label>
                            <Form.Control
                                type="text"
                                name="referral_code"
                                value={updatedDetails?.referral_code || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Referred By</Form.Label>
                            <Form.Control
                                type="text"
                                name="referred_by"
                                value={updatedDetails?.referred_by || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Aadhar Number</Form.Label>
                            <Form.Control
                                type="text"
                                name="aadhar_number"
                                value={updatedDetails?.aadhar_number || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Middle Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="middle_name"
                                value={updatedDetails?.middle_name || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Pincode</Form.Label>
                            <Form.Control
                                type="text"
                                name="pincode"
                                value={updatedDetails?.pincode || ""}
                                onChange={handleChange}
                            />
                        </Form.Group>

                        <Button variant="primary" onClick={handleSaveChanges} className="mt-3">
                            Save Changes
                        </Button>
                    </Form>
                </Row>
            </Block>
        </Layout>
    );
}

export default MyProfilePage;
